import { publishErrorMessage } from '@hooks/use-cap-signals';
import { type SerializedData } from 'bernie-core';
import { type Logger, NOOP_LOGGER } from 'bernie-logger';
import { Store } from 'bernie-plugin-mobx';
import { makeObservable, action, observable } from 'mobx';

export interface IBanner {
  headline: string;
  message: string;
  type: 'error' | 'success';
  regex: string;
  buttonText?: string;
  multiAction?: {
    actionText?: string;
    actionUrl?: string;
    dismissText?: string;
  };
}

export class BannerStore extends Store {
  @observable banners: Array<IBanner> = [];

  constructor(config, logger: Logger = NOOP_LOGGER) {
    super({}, logger);
    makeObservable(this);
  }

  hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  @action
  addMessage(
    headline: string,
    message: string,
    type: 'error' | 'success',
    regex: string,
    options?: string | { actionText: string; actionUrl: string; dismissText?: string },
  ) {
    if (typeof options === 'string') {
      this.banners.push({ headline, message, type, regex, buttonText: options });
    } else if (options) {
      this.banners.push({
        headline,
        message,
        type,
        regex,
        multiAction: options,
      });
    } else {
      this.banners.push({ headline, message, type, regex });
    }
    if (type === 'error') {
      const combinedMessage =
        [headline, message]
          .map((s) => s?.trim())
          .filter(Boolean)
          .join(' - ') || 'Unknown error';
      publishErrorMessage('ERROR_BANNER', undefined, new Error(combinedMessage, { cause: regex }));
    }
  }

  get errorMessages() {
    return this.banners.filter((message) => message.type === 'error');
  }

  get successMessages() {
    return this.banners.filter((message) => message.type === 'success');
  }

  @action
  clearMessages() {
    this.banners = [];
  }

  clearErrorMessages() {
    this.banners = this.banners.filter((message) => message.type !== 'error');
  }

  clearSuccessMessages() {
    this.banners = this.banners.filter((message) => message.type !== 'success');
  }
}
